/**
 * API Service Module
 * 
 * Centralized service for all API calls to the backend.
 * Handles authentication and provides methods for all application endpoints.
 */

import axios from 'axios';

// =============================================================================
// CONFIGURATION
// =============================================================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// =============================================================================
// AUTHENTICATION HELPERS
// =============================================================================

/** Get ID token from MSAL (Azure AD JWT, RS256 signed, verifiable by backend). */
const getAccessToken = async (msalInstance) => {
  if (!msalInstance) return null;
  
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;
  
  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: ["User.Read", "profile", "email", "openid"],
      account: accounts[0]
    });
    return tokenResponse.idToken;
  } catch (error) {
    console.error("Token acquisition failed:", error);
    return null;
  }
};

const getAuthHeaders = (token) => {
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// =============================================================================
// CLIENT SELECTOR (Super Admin only)
// =============================================================================

export const getAvailableClients = async (msalInstance) => {
  const token = await getAccessToken(msalInstance);
  if (!token) return null;
  
  try {
    const response = await axios.get(`${API_BASE_URL}/api/available-clients`, {
      headers: getAuthHeaders(token)
    });
    return response.data.clients;
  } catch (error) {
    console.error('Error fetching available clients:', error);
    throw error;
  }
};

// =============================================================================
// USER MANAGEMENT
// =============================================================================

export const getUserInfo = async (msalInstance) => {
  const token = await getAccessToken(msalInstance);
  if (!token) return null;
  
  try {
    const response = await axios.get(`${API_BASE_URL}/api/user`, {
      headers: getAuthHeaders(token)
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
};

// =============================================================================
// ORDER MANAGEMENT
// =============================================================================

/**
 * Fetch orders for a date range.
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {object} msalInstance
 * @param {number|null} selectedClientId - client ID for Super Admin client switching
 */
export const fetchOrders = async (startDate, endDate, msalInstance, selectedClientId = null) => {
  try {
    const token = await getAccessToken(msalInstance);
    
    const start = new Date(startDate); start.setHours(0, 0, 0, 0);
    const end   = new Date(endDate);   end.setHours(23, 59, 59, 999);

    const body = {
      start_date: start.toISOString(),
      end_date: end.toISOString(),
    };
    if (selectedClientId) body.selected_client_id = selectedClientId;

    const response = await axios.post(`${API_BASE_URL}/orders`, body, {
      headers: getAuthHeaders(token)
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching orders:', error.response ? error.response.data : error.message);
    throw error;
  }
};

export const fetchOrderDetails = async (orderId, msalInstance, selectedClientId = null) => {
  const token = await getAccessToken(msalInstance);
  try {
    const params = selectedClientId ? { selected_client_id: selectedClientId } : {};
    const response = await axios.get(`${API_BASE_URL}/orders/${orderId}`, {
      headers: getAuthHeaders(token),
      params
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching order details:', error);
    throw error;
  }
};

export const downloadInvoice = async (orderId, msalInstance) => {
  const token = await getAccessToken(msalInstance);
  try {
    const response = await axios.get(`${API_BASE_URL}/orders/${orderId}/invoice`, {
      headers: getAuthHeaders(token),
      responseType: 'blob'
    });
    
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${orderId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('Error downloading invoice:', error);
    throw error;
  }
};

// =============================================================================
// SHARED LINKS
// =============================================================================

export const createShareLink = async (orderId, recipientEmail, durationHours, msalInstance) => {
  try {
    const token = await getAccessToken(msalInstance);
    const response = await axios.post(`${API_BASE_URL}/orders/${orderId}/share`, { 
        recipient_email: recipientEmail, 
        duration_hours: durationHours 
      },
      {
        headers: getAuthHeaders(token)
      }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to create share link:', error);
    throw error;
  }
};

export const fetchOrderByToken = async (token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/shared-links/${token}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching order by token:', error);
    throw error;
  }
};

/**
 * Fetch comprehensive order summary for shared links
 * This is the main endpoint used by SharedLinkPage
 */
export const fetchSharedSummary = async (token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/shared-links/${token}/summary`);
    return response.data;
  } catch (error) {
    console.error('Error fetching shared summary:', error);
    throw error;
  }
};

// =============================================================================
// ADMIN PORTAL (Super Admin only)
// =============================================================================

const adminRequest = async (method, path, msalInstance, data = null) => {
  const token = await getAccessToken(msalInstance);
  const config = { headers: { Authorization: `Bearer ${token}` } };
  if (method === 'get' || method === 'delete') {
    const response = await axios[method](`${API_BASE_URL}${path}`, config);
    return response.data;
  }
  const response = await axios[method](`${API_BASE_URL}${path}`, data, config);
  return response.data;
};

export const adminListUsers = (i) => adminRequest('get', '/api/admin/users', i);
export const adminCreateUser = (i, data) => adminRequest('post', '/api/admin/users', i, data);
export const adminUpdateRole = (i, email, roleid) =>
  adminRequest('put', `/api/admin/users/${encodeURIComponent(email)}/role`, i, { roleid });
export const adminUpdateUserClient = (i, email, clientId) =>
  adminRequest('put', `/api/admin/users/${encodeURIComponent(email)}/client`, i, { client_id: clientId });
export const adminToggleUser = (i, email) =>
  adminRequest('put', `/api/admin/users/${encodeURIComponent(email)}/toggle`, i, {});
export const adminDeleteUser = (i, email) =>
  adminRequest('delete', `/api/admin/users/${encodeURIComponent(email)}`, i);
export const adminGetAccess = (i, email) =>
  adminRequest('get', `/api/admin/users/${encodeURIComponent(email)}/access`, i);
export const adminAddAccess = (i, email, data) =>
  adminRequest('post', `/api/admin/users/${encodeURIComponent(email)}/access`, i, data);
export const adminUpdateAccess = (i, id, type) =>
  adminRequest('put', `/api/admin/access/${id}`, i, { access_type: type });
export const adminDeleteAccess = (i, id) =>
  adminRequest('delete', `/api/admin/access/${id}`, i);
export const adminListClients = (i) => adminRequest('get', '/api/admin/clients', i);
export const adminCreateClient = (i, data) =>
  adminRequest('post', '/api/admin/clients', i, data);
export const adminUpdateClient = (i, clientId, data) =>
  adminRequest('put', `/api/admin/clients/${clientId}`, i, data);
export const adminDeleteClient = (i, clientId) =>
  adminRequest('delete', `/api/admin/clients/${clientId}`, i);
export const adminListRoles = (i) => adminRequest('get', '/api/admin/roles', i);
export const adminAddTmId = (i, clientId, tmId) =>
  adminRequest('post', `/api/admin/clients/${clientId}/tmids`, i, { tm_id: tmId });
export const adminDeleteTmId = (i, tmidRowId) =>
  adminRequest('delete', `/api/admin/tmids/${tmidRowId}`, i);

// =============================================================================
// GEOCODING & MAPPING
// =============================================================================

export const reverseGeocode = async (address, msalInstance) => {
  const token = await getAccessToken(msalInstance);
  try {
    const response = await axios.post(`${API_BASE_URL}/reverse-geocode`, {
      address: address
    }, {
      headers: getAuthHeaders(token)
    });
    return response.data; // { lat: ..., lon: ... }
  } catch (error) {
    console.error('Error reverse geocoding address:', address, error);
    return null;
  }
};

export const estTimeCalc = async (startLat, startLon, endLat, endLon, vehicleNum, msalInstance) => {
  const token = await getAccessToken(msalInstance);
  try {
    const response = await axios.post(`${API_BASE_URL}/eta`, {
      start_lat: startLat,
      start_lon: startLon,
      end_lat: endLat,
      end_lon: endLon,
      vehicle_name: vehicleNum
    }, {
      headers: getAuthHeaders(token)
    });
    return response.data; // { distance_km: X.XX, duration_min: Y.YY }
  } catch (error) {
    console.error('Error calculating ETA:', error);
    return null;
  }
};

export const getTruckRoute = async (vehicleNum, startTime, endTime, msalInstance) => {
  const token = await getAccessToken(msalInstance);
  try {
    const response = await axios.get(`${API_BASE_URL}/route/truck`, {
      params: {
        vehicle_num: vehicleNum,
        start_time: startTime,
        end_time: endTime
      },
      headers: getAuthHeaders(token)
    });
    return response.data.routeCoordinates || [];
  } catch (error) {
    console.error('Error fetching truck route:', error);
    return [];
  }
};



